// Per-class roster-sheet policy, shared by the renderer (art/draw.ts) and the
// loader (core/assets.ts) so the two can never disagree about which sheets are
// drawable. Pure data + predicates — no DOM — so unit tests run in node.

/** The six per-class sheet slots: public/assets/roster/<id>_<sheet>.png. */
export type RosterSheet = "idle" | "attack" | "special" | "talon" | "wing" | "beak";

export const ROSTER_SHEETS: readonly RosterSheet[] = [
  "idle",
  "attack",
  "special",
  "talon",
  "wing",
  "beak",
];

/** The three RPS throw sheets (lazily loaded per match via Assets.loadThrows). */
export const THROW_SHEETS: readonly RosterSheet[] = ["talon", "wing", "beak"];

// Deny-list for sheets that must not render OR be fetched. EMPTY by owner
// decision (2026-07-19): every class renders ITS OWN sheet at every beat —
// above all the talon/wing/beak reveal — because a mid-fight character swap to
// the shared base mascot reads as a bug. The shared fighter_rock/paper/scissors
// art is strictly a failed-load fallback now.
//
// REPAINT BACKLOG (2026-07 art audit; accepted-for-now, queued for the
// assets-src pipeline — tools/build-workflow.js — re-add an entry here ONLY
// while a repaint is actively broken):
//   thundercluck  — idle/talon/wing/beak are flat anime-duck style vs the
//                   photoreal plush attack/special (species/style flip mid-fight)
//   frostfeather  — wing is a white armored angel-bird, not the blue hoodie kid
//   dawncrower    — talon/wing/beak are three different crowns/costumes/faces
//   cannoneer     — talon/wing panda-mask + beak cutesy face vs the idle mascot
//   emberplume    — beak has purple lightning (storm motif) on the fire class;
//                   wing is a cutesy duck face that breaks the mascot language
//   spurblade     — wing wields sickles (reads attack, not paper); beak swaps
//                   to a long duck-bill head
export const SHEET_EXCLUSIONS: Record<string, readonly RosterSheet[]> = {};

/** True when (classId, sheet) is deny-listed from rendering AND fetching. */
export function sheetExcluded(classId: string, sheet: RosterSheet): boolean {
  return SHEET_EXCLUSIONS[classId]?.includes(sheet) ?? false;
}

/**
 * In-class fallback order for a sheet (first loadable, non-excluded entry
 * renders):
 *   throws          → self, then the class ATTACK sheet (still per-class art;
 *                     the RPS hand reads via the drawHandSign reveal badge),
 *                     then out to the shared base rock/paper/scissors art —
 *                     NEVER the idle sheet, because a fighter standing idle
 *                     mid-reveal reads as "no throw happened".
 *   attack/special  → self, then idle.
 *   idle            → itself only.
 */
export function sheetFallbackChain(sheet: RosterSheet): readonly RosterSheet[] {
  switch (sheet) {
    case "talon":
    case "wing":
    case "beak":
      return [sheet, "attack"];
    case "attack":
      return ["attack", "idle"];
    case "special":
      return ["special", "idle"];
    default:
      return ["idle"];
  }
}
